/**
 * Markdown arrives from the model one token at a time, so for a few frames the
 * renderer is handed a string whose inline markup is cut in half. remark has to
 * treat those halves as literal text — there is no other reading of them — so a
 * name the model is writing as `**[Jomfruland](/steder/jomfruland)**` is on
 * screen as the raw characters `**[Jomfruland](/steder/jom` before it snaps into
 * a bold link. The visitor sees punctuation and a url flash past, and every
 * line after it jumps as the text reflows.
 *
 * The fix is to close what is still open before rendering: an unfinished `**`
 * gets its closing pair, so the word is bold from its first character rather
 * than flipping to bold once the model catches up, and an unfinished link is
 * reduced to its label, which is exactly the text the finished link will show.
 * Both leave the rendered glyphs identical to the ones the completed markup
 * produces — only colour and weight arrive late, and neither moves anything.
 */

/** Inline markers we close. Ordered longest-first so `**` wins over `*`. */
const INLINE_MARKERS = ['```', '~~', '**', '`'];

/** True when the character at `index` is escaped by an odd run of backslashes. */
function isEscaped(text, index) {
  let slashes = 0;
  for (let i = index - 1; i >= 0 && text[i] === '\\'; i -= 1) slashes += 1;
  return slashes % 2 === 1;
}

/**
 * The markers left open at the end of the string, outermost first. A fence or a
 * code span swallows everything until it closes — markup inside code is text,
 * not markup — so while one is open nothing else is tracked.
 * @param {string} text
 * @returns {string[]}
 */
function openMarkers(text) {
  /** @type {string[]} */
  const open = [];

  for (let i = 0; i < text.length; ) {
    const inCode = open[open.length - 1] === '```' || open[open.length - 1] === '`';
    const marker = INLINE_MARKERS.find((candidate) => text.startsWith(candidate, i));

    if (!marker || isEscaped(text, i)) {
      i += 1;
      continue;
    }

    // Inside code only that same marker can end it; ``` must not be read as a
    // ` that closes an open code span, hence the longest-first ordering above.
    if (inCode) {
      if (marker === open[open.length - 1]) open.pop();
      i += marker.length;
      continue;
    }

    if (open[open.length - 1] === marker) open.pop();
    else open.push(marker);
    i += marker.length;
  }

  return open;
}

/**
 * Drop a `**` that opens immediately before the link being written.
 *
 * `**[Navn](url)**` is the model's habit in almost every list item, and bold
 * wrapping nothing but a link renders unemphasised — the link carries the
 * emphasis on its own (see the `strong:has(> a:only-child)` rule in
 * _chatbot.scss). While the url is still arriving there is no `a` for that rule
 * to match, so leaving the `**` in place would set the label in the heavier
 * weight and then drop it the moment the link closed.
 *
 * Only an opener the label is flush against counts: in `**flotte turer til
 * [Rjukan](url)**` the bold really does span more than the link, and it reads
 * the same before and after the url lands.
 * @param {string} prefix text preceding the `[`
 * @returns {string}
 */
function dropEmphasisHuggingLink(prefix) {
  if (!prefix.endsWith('**') || isEscaped(prefix, prefix.length - 2)) return prefix;
  const open = openMarkers(prefix);
  if (open[open.length - 1] !== '**') return prefix;
  return prefix.slice(0, -2);
}

/**
 * Cut an unfinished link or image back to its label. `[Jomfruland](/steder/jom`
 * becomes `Jomfruland`, and a bare `[Jomfrul` becomes `Jomfrul` — in both cases
 * the characters the finished link will show, and nothing else.
 *
 * Only the last bracket group is considered: anything before it has already
 * closed, and a `[` inside a code span is left alone.
 * @param {string} text
 * @returns {string}
 */
function trimUnfinishedLink(text) {
  const open = openMarkers(text);
  if (open.includes('```') || open.includes('`')) return text;

  const start = text.lastIndexOf('[');
  if (start === -1 || isEscaped(text, start)) return text;

  const rest = text.slice(start);
  const close = rest.indexOf(']');

  // No closing bracket yet: everything from `[` on is the label being written.
  if (close === -1) return dropEmphasisHuggingLink(text.slice(0, start)) + rest.slice(1);

  const after = rest.slice(close + 1);
  // The label is closed and the destination has not started, or has started and
  // not finished. A closed `](…)` is a finished link and stays as it is.
  if (after === '' || (after.startsWith('(') && !after.includes(')'))) {
    const label = rest.slice(1, close);
    // An image renders as its alt text until the url lands, so drop the `!` too.
    const prefix = text.slice(0, start).replace(/!$/, '');
    return dropEmphasisHuggingLink(prefix) + label;
  }

  return text;
}

/**
 * Rewrite a half-streamed markdown string so it renders as what it is going to
 * be. A complete string is returned unchanged, so this is safe to run on every
 * frame of a stream and on the finished text alike.
 * @param {string} text
 * @returns {string}
 */
export default function stabilizeStreamingMarkdown(text) {
  if (!text) return text;

  const trimmed = trimUnfinishedLink(text);
  const open = openMarkers(trimmed);
  if (!open.length) return trimmed;

  // Close innermost first, and give a fence the newline it needs to end.
  const closers = open
    .slice()
    .reverse()
    .map((marker) => (marker === '```' ? '\n```' : marker))
    .join('');

  // The closer goes after the last character the model has actually written,
  // not after the space that follows it: CommonMark refuses to end emphasis on
  // whitespace, so `**Dammane **` is not bold — it is three literal asterisks
  // on screen, which is the flicker this whole function exists to avoid.
  const spacing = /\s*$/.exec(trimmed)[0];
  const body = spacing ? trimmed.slice(0, -spacing.length) : trimmed;

  return body + closers + spacing;
}
